// Protoboards: completa (830 puntos), media (400) y mini (170).
// Columnas numeradas de izquierda a derecha; filas a–e arriba y f–j abajo del canal central.
// Rieles: tn/tp arriba (− y +), bp/bn abajo (+ y −), continuos a lo largo de la placa.
import { definePart, PITCH } from './registry.js';

export const BB_H = 8.5;
export const BB_ROWS = 'abcdefghij';

export const BB_SIZES = {
  full: { name: 'Completa (830)', cols: 63, groups: 10, w: 165.1, d: 54.6 },
  half: { name: 'Media (400)', cols: 30, groups: 5, w: 82.55, d: 54.6 },
  mini: { name: 'Mini (170)', cols: 17, groups: 0, w: 46.99, d: 35.56 },
};

// Los rieles quedan en la misma rejilla de medio paso que las filas (a 3 pasos de la fila a/j),
// así una pieza vertical puede ir de una fila al riel.
export const RAILS = [
  { id: 'tn', z: -9.5 * PITCH, sign: '-' },
  { id: 'tp', z: -8.5 * PITCH, sign: '+' },
  { id: 'bp', z: 8.5 * PITCH, sign: '+' },
  { id: 'bn', z: 9.5 * PITCH, sign: '-' },
];

export function bbSize(props) {
  return BB_SIZES[props.size] || BB_SIZES.half;
}

export function rowZ(r) {
  return r < 5 ? (r - 4) * PITCH - 1.5 * PITCH : (r - 5) * PITCH + 1.5 * PITCH;
}

export function colX(c, cols) {
  return (c - (cols + 1) / 2) * PITCH;
}

/** Posición x de los agujeros del riel (índice j desde 0), alineados con las columnas. */
export function railHoles(size) {
  if (!size.groups) return [];
  const positions = size.groups * 6 - 1;
  const offset = Math.round((size.cols - positions) / 2) + 1;
  const holes = [];
  for (let g = 0; g < size.groups; g++) {
    for (let k = 0; k < 5; k++) {
      const idx = g * 6 + k;
      holes.push({ n: g * 5 + k + 1, col: idx + offset, x: colX(idx + offset, size.cols) });
    }
  }
  return holes;
}

function bbPins(props) {
  const size = bbSize(props);
  const pins = [];
  for (let c = 1; c <= size.cols; c++) {
    for (let r = 0; r < 10; r++) {
      const name = BB_ROWS[r] + c;
      pins.push({ name, label: `${BB_ROWS[r].toUpperCase()}${c}`, kind: 'hole', x: colX(c, size.cols), y: BB_H, z: rowZ(r), hole: true });
    }
  }
  for (const rail of RAILS) {
    if (!size.groups) break;
    for (const h of railHoles(size)) {
      pins.push({
        name: rail.id + h.n,
        label: `Riel ${rail.sign === '+' ? '+' : '−'} ${rail.id[0] === 't' ? 'superior' : 'inferior'}`,
        kind: rail.sign === '+' ? 'power' : 'gnd',
        x: h.x, y: BB_H, z: rail.z, hole: true,
      });
    }
  }
  return pins;
}

function bbBuses(props) {
  const size = bbSize(props);
  const buses = [];
  for (let c = 1; c <= size.cols; c++) {
    buses.push(['a', 'b', 'c', 'd', 'e'].map((r) => r + c));
    buses.push(['f', 'g', 'h', 'i', 'j'].map((r) => r + c));
  }
  if (size.groups) {
    const holes = railHoles(size);
    for (const rail of RAILS) buses.push(holes.map((h) => rail.id + h.n));
  }
  return buses;
}

/** Agujero bajo un punto local (x, z) o null si no cae en ninguno (tolerancia en mm). */
export function holeAt(props, x, z, tol = 0.9) {
  const size = bbSize(props);
  const c = Math.round(x / PITCH + (size.cols + 1) / 2);
  if (c >= 1 && c <= size.cols && Math.abs(colX(c, size.cols) - x) <= tol) {
    for (let r = 0; r < 10; r++) {
      if (Math.abs(rowZ(r) - z) <= tol) return BB_ROWS[r] + c;
    }
  }
  if (size.groups) {
    for (const rail of RAILS) {
      if (Math.abs(rail.z - z) > tol) continue;
      for (const h of railHoles(size)) if (Math.abs(h.x - x) <= tol) return rail.id + h.n;
    }
  }
  return null;
}

definePart({
  type: 'breadboard',
  name: 'Protoboard',
  category: 'prototipado',
  description: 'Placa de pruebas sin soldadura. Los componentes que se colocan encima quedan conectados a sus agujeros.',
  kind: 'breadboard',
  props: [
    { key: 'size', label: 'Tamaño', type: 'select', default: 'half', structural: true,
      options: Object.entries(BB_SIZES).map(([value, s]) => ({ value, label: s.name })) },
  ],
  variants: [
    { label: 'Protoboard media (400 puntos)', props: { size: 'half' } },
    { label: 'Protoboard completa (830 puntos)', props: { size: 'full' } },
    { label: 'Protoboard mini (170 puntos)', props: { size: 'mini' } },
  ],
  pins: bbPins,
  buses: bbBuses,
  footprint: (props) => { const s = bbSize(props); return { w: s.w, d: s.d, h: BB_H }; },
  surfaceY: BB_H,
  holeAt,
});
